<template>
    <v-container
        v-resize="onResize"
        ref="container"
        max-width="1200"
        :style="{ height: '100%' }"
        class="overflow-y-hidden pa-0"
    >
        <v-card class="mx-auto">
            <v-card-title>
                <v-btn
                    class="mr-3"
                    depressed
                    color="primary"
                    icon
                    rounded
                    @click="close"
                >
                    <v-icon>mdi-close</v-icon>
                </v-btn>
                <h3 class="ml-2">
                    Processes {{ item ? '(' + item.partNumber + ')' : '' }}
                </h3>
                <v-spacer></v-spacer>
                <v-btn
                    rounded
                    color="primary"
                    class="white--text"
                    :loading="loading"
                    @click="saveProcesses"
                    :disabled="usedProcesses.length <= 0"
                >
                    SAVE
                </v-btn>
            </v-card-title>
            <v-divider class="mx-4" />
            <v-card-text class="ml-2">
                <v-alert
                    v-if="showDisabledProcessesWarning"
                    dense
                    outlined
                    type="error"
                    class="mb-4"
                >
                    WARNING: The following processes are disabled and were
                    removed from the process list:
                    <strong>{{ disabledProcessNames.join(', ') }}</strong
                    >.
                </v-alert>
                <template>
                    <v-row class="d-flex justify-start" no-gutters>
                        <v-col cols="7">
                            <v-sheet class="fill-height">
                                <h4 class="primary--text my-2">Use</h4>
                                <draggable
                                    class="list-group overflow-y-auto px-2 py-0 ma-0"
                                    :style="{
                                        height: `${height}px`,
                                        width: '100%',
                                    }"
                                    v-model="usedProcesses"
                                    v-bind="dragOptions"
                                >
                                    <template>
                                        <div
                                            v-for="(process,
                                            index) in usedProcesses"
                                            :key="index"
                                            class="mx-0 my-0 d-flex align-center"
                                            draggable
                                        >
                                            <v-chip
                                                class="full-width-chip mb-3"
                                            >
                                                <span class="mr-2">{{
                                                    index + 1 + ')'
                                                }}</span>
                                                {{ process.name }}
                                            </v-chip>
                                            <v-btn
                                                v-if="
                                                    index <
                                                        usedProcesses.length - 1
                                                "
                                                class="ml-3 mt-n3"
                                                depressed
                                                :color="
                                                    process.cancelSend
                                                        ? 'red'
                                                        : 'primary'
                                                "
                                                icon
                                                rounded
                                                @click="changeCancelSend(index)"
                                            >
                                                <v-icon
                                                    v-if="process.cancelSend"
                                                >
                                                    mdi-download-off-outline
                                                </v-icon>
                                                <v-icon v-else>
                                                    mdi-download
                                                </v-icon>
                                            </v-btn>
                                        </div>
                                    </template>
                                </draggable>
                            </v-sheet>
                        </v-col>
                        <v-col cols="5">
                            <div>
                                <h4 class="secondary--text my-2">
                                    Processes
                                </h4>
                                <draggable
                                    v-model="unusedProcesses"
                                    :tag="'tbody'"
                                    v-bind="dragOptions"
                                >
                                    <template>
                                        <div
                                            v-for="(process,
                                            index) in unusedProcesses"
                                            :key="index"
                                            class="mx-0 my-0 d-flex align-center"
                                            draggable
                                        >
                                            <v-chip
                                                class="full-width-chip mb-3"
                                            >
                                                {{ process.name }}
                                            </v-chip>
                                        </div>
                                    </template>
                                </draggable>
                            </div>
                        </v-col>
                    </v-row>
                </template>
            </v-card-text>
        </v-card>
        <v-dialog v-model="showDisabledProcessesConfirmDialog" max-width="520">
            <v-card>
                <v-card-title class="text-h6">Confirm edit</v-card-title>
                <v-card-text>
                    The item includes disabled processes (<strong>{{
                        disabledProcessNames.join(', ')
                    }}</strong
                    >). They were removed from the process list. Do you want to
                    continue saving this edit?
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        text
                        @click="showDisabledProcessesConfirmDialog = false"
                    >
                        Cancel
                    </v-btn>
                    <v-btn
                        color="primary"
                        text
                        @click="confirmSaveProcesses"
                    >
                        Continue
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import draggable from 'vuedraggable'
import API from '@/services/api'
import _ from 'lodash'
import { mapMutations } from 'vuex'

export default {
    name: 'ArrangeProcesses',
    components: {
        draggable,
    },
    props: {
        item: {
            type: Object,
            default: () => {},
        },
        originalItem: {
            type: Object,
            default: () => {},
        },
        workOrder: {
            type: Object,
            default: () => {},
        },
        settingsProcesses: {
            type: Object,
            required: true,
            default: () => [],
        },
        multipleItemSelect: {
            type: Boolean,
            default: () => false,
        },
        itemsProcesses: {
            type: Array,
            default: () => [],
        },
    },
    data: () => ({
        selection: null,
        currentTag: null,
        usedProcesses: [],
        unusedProcesses: [],
        draggedProcess: null,
        height: 240,
        dragOptions: {
            group: 'processes',
            animation: 150,
        },
        loading: false,
        disabledProcessNames: [],
        showDisabledProcessesConfirmDialog: false,
        disabledProcessesWarningConfirmed: false,
    }),
    computed: {
        showDisabledProcessesWarning() {
            return this.disabledProcessNames.length > 0
        },
    },
    mounted() {
        try {
            const allProcesses = this.settingsProcesses.processes || []
            const enabledProcesses = this.getEnabledProcesses(allProcesses)
            const allProcessesById = new Map(allProcesses.map(p => [p.id, p]))

            if (this.multipleItemSelect) {
                const sourceProcessIds = (this.itemsProcesses || []).map(p =>
                    typeof p === 'object' ? p?.id : p
                )
                this.disabledProcessNames = [
                    ...new Set(
                        sourceProcessIds
                            .filter(
                                id =>
                                    id &&
                                    !enabledProcesses.some(p => p.id === id)
                            )
                            .map(id => allProcessesById.get(id)?.name || id)
                    ),
                ]
                this.usedProcesses = _.cloneDeep(
                    this.getEnabledProcesses(this.itemsProcesses)
                )
                const usedIds = new Set(this.usedProcesses.map(p => p.id))
                this.unusedProcesses = enabledProcesses.filter(
                    process => !usedIds.has(process.id)
                )
            } else {
                const itemProcesses = this.item.processes || []
                // Keep track of disabled process IDs so users know why they disappeared.
                this.disabledProcessNames = [
                    ...new Set(
                        itemProcesses
                            .map(p => (typeof p === 'object' ? p?.id : p))
                            .filter(
                                id =>
                                    id &&
                                    !enabledProcesses.some(p => p.id === id)
                            )
                            .map(id => allProcessesById.get(id)?.name || id)
                    ),
                ]
                itemProcesses.forEach(itemProcess => {
                    const foundProcess = enabledProcesses.find(
                        process => process.id === itemProcess
                    )
                    if (foundProcess) {
                        this.usedProcesses.push(foundProcess)
                    }
                })
                this.unusedProcesses = enabledProcesses.filter(
                    process => !itemProcesses.includes(process.id)
                )
            }
            this.onResize()
        } catch (error) {
            console.error(error)
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        async saveProcesses() {
            if (
                this.showDisabledProcessesWarning &&
                !this.disabledProcessesWarningConfirmed
            ) {
                this.showDisabledProcessesConfirmDialog = true
                return
            }
            await this.executeSaveProcesses()
        },
        async confirmSaveProcesses() {
            this.disabledProcessesWarningConfirmed = true
            this.showDisabledProcessesConfirmDialog = false
            await this.executeSaveProcesses()
        },
        async executeSaveProcesses() {
            try {
                if (!this.multipleItemSelect) {
                    this.loading = true
                    await API.updateItemProcesses({
                        workOrderId: this.workOrder.id,
                        partNumberId: this.item.partNumberId,
                        processes: this.usedProcesses.map(
                            process => process.id
                        ),
                        deliveryOptions: this.usedProcesses.map(
                            (process, index) => ({
                                id: process.id,
                                // Si es el último proceso, asigna false a cancelSend
                                cancelSend:
                                    index === this.usedProcesses.length - 1
                                        ? false
                                        : process.cancelSend || false,
                            })
                        ),
                        settingProcessesId: this.settingsProcesses.id,
                    })
                }
                this.$emit('saveProcesses', this.usedProcesses)
                this.loading = true
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        changeCancelSend(index) {
            if (typeof this.usedProcesses[index].cancelSend === 'undefined') {
                this.$set(this.usedProcesses[index], 'cancelSend', true)
            } else {
                this.usedProcesses[index].cancelSend = !this.usedProcesses[
                    index
                ].cancelSend
            }
            this.usedProcesses = _.cloneDeep(this.usedProcesses)
        },
        onResize() {
            const {
                container: { clientHeight: containerHeight },
            } = this.$refs
            this.height = containerHeight - 62
        },
        close() {
            this.$emit('closeDialog')
        },
        getEnabledProcesses(processes) {
            if (!Array.isArray(processes)) return []
            return processes.filter(process => process?.enabled !== false)
        },
    },
}
</script>

<style scoped>
.full-width-chip {
    width: 150px;
}
</style>
